import { Factory } from "@/data/dao/Factory";
import { OrderDao } from "@/data/dao/service/OrderDao";
import { CancellationState, OrderState } from "@/data/model/Enums";
import type { IOrder } from "@/data/model/Order";
import { role } from "@/data/util/Config";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";

import { translate } from "@/shared/localization/language";

import { i18n, isInRole } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { portalEvents } from "@/portal/events/portalEvents";
import { OrderExportWindow } from "@/portal/modal/OrderExportWindow";
import { SalesReportExportWindow } from "@/portal/modal/SalesReportExportWindow";

export class OrderTableController {
    private $element: JQuery;
    private dao: OrderDao;
    private dataTable: DataTable<IOrder>;
    private search: TableFilter;

    constructor(element: JQuery) {
        this.$element = element;
        this.dao = Factory.getInstance<OrderDao>(OrderDao);

        //bind export btns
        this.$element.find("> .kt-portlet__head > .kt-portlet__head-toolbar .btn.orderexport").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            new OrderExportWindow().show();
        });

        this.$element.find("> .kt-portlet__head > .kt-portlet__head-toolbar .btn.salesreportexport").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            new SalesReportExportWindow().show();
        });

        console.log(i18n("order.fields.fulfillDate"));

        const tableConfig: IDataTableConfig<IOrder> = {
            columnMapping: [
                {
                    name: i18n("order.fields.fulfillDate"),
                    property: "confirmedDateTime",
                    styles: { width: "9%" },
                    formatter: (m, p) =>
                        m.confirmedDateTime == null
                            ? DateTime.toPreciseDateTimeString(m.created)
                            : DateTime.toPreciseDateTimeString(p),
                },
                {
                    name: i18n("order.fields.orderNumber"),
                    property: "orderNumber",
                    styles: { width: "9%" },
                },
                {
                    name: i18n("order.fields.dailyOrderIncrement"),
                    property: "dailyOrderIncrement",
                    styles: { width: "9%" },
                },
                {
                    name: i18n("general.fields.state"),
                    property: "state",
                    sortable: false,
                    styles: { width: "8%" },
                    formatter: (m, p) => {
                        let styleClass = "kt-badge--primary";
                        switch (p) {
                            case OrderState.Closed:
                                styleClass = "kt-badge--dark";
                                break;
                            case OrderState.FullyPaid:
                                styleClass = "kt-badge--success";
                                break;
                            case OrderState.Open:
                                styleClass = "kt-badge--warning";
                                break;
                            case OrderState.Canceled:
                                styleClass = "kt-badge--danger";
                                break;
                            case OrderState.TimedOut:
                                styleClass = "kt-badge--dark";
                                break;
                            case OrderState.NotPaid:
                                styleClass = "kt-badge--red";
                                break;
                            case OrderState.ClosedNotPaid:
                                styleClass = "kt-badge--red";
                                break;
                        }

                        return (
                            '<div class="kt-badge ' +
                            styleClass +
                            ' kt-badge--inline">' +
                            i18n("enums.OrderState." + OrderState[p]) +
                            "</div>"
                        );
                    },
                },
                {
                    name: i18n("order.fields.cancellation"),
                    property: "cancellationState",
                    sortable: false,
                    styles: { width: "8%" },
                    formatter: (m, p) => {
                        if (p === undefined) return "";

                        return (
                            '<div class="kt-badge kt-badge--danger kt-badge--inline">' +
                            i18n("enums.CancellationState." + CancellationState[p]) +
                            "</div>"
                        );
                    },
                },
                {
                    name: i18n("order.fields.useCase"),
                    property: "useCaseName",
                    sortable: false,
                    formatter: (m, p) => {
                        if (p === undefined) return "";

                        return Format.htmlEscape(translate(p));
                    },
                },
                {
                    name: i18n("general.entities.table"),
                    property: "tableName",
                    sortable: false,
                    styles: { width: "10%" },
                    formatter: (m) => {
                        if (m.tableId === undefined) return i18n("order.deletedTable");

                        if (isInRole(role.Admin) || isInRole(role.Manager)) {
                            return $(
                                '<a href="/table/' + m.tableId + '">' + Format.htmlEscape(m.tableName) + "</a>"
                            ).click((e) => {
                                e.stopPropagation();
                            });
                        } else return Format.htmlEscape(m.tableName);
                    },
                },
                {
                    name: i18n("order.fields.scheduled"),
                    property: "scheduledFulfillmentDateTime",
                    styles: { width: "9%" },
                    formatter: (m) => {
                        if (m.scheduledFulfillmentDateTime == null) return "-";
                        else {
                            return DateTime.toPreciseDateTimeString(m.scheduledFulfillmentDateTime);
                        }
                    },
                },
                {
                    name: i18n("order.fields.total"),
                    property: "consumption",
                    styles: { width: "11%", "text-align": "right" },
                    formatter: (m, p) => Format.toMoneyFormat(p + m.tip + m.deliveryFee),
                },
                {
                    name: i18n("general.entities.user"),
                    property: "userName",
                    sortable: false,
                    styles: { width: "9%", "text-align": "right" },
                    formatter: (m) => {
                        if (m.identityId == null) {
                            return m.userName === undefined ? i18n("general.deletedUser") : Format.htmlEscape(m.userName);
                        }

                        if (isInRole(role.Admin)) {
                            if (m.userName === "Anonymer User") {
                                return $(`<a href="/user/${m.identityId}">${i18n("general.anonymousUser")}</a>`).click((e) => {
                                    e.stopPropagation();
                                });
                            }

                            return $('<a href="/user/' + m.identityId + '">' + m.userName + "</a>").click((e) => {
                                e.stopPropagation();
                            });
                        } else {
                            if (m.userName === "Anonymer User") {
                                return Format.htmlEscape(i18n("general.anonymousUser"));
                            } else {
                                return m.userName;
                            }
                        }
                    },
                },
                {
                    name: "",
                    property: "id",
                    sortable: false,
                    styles: { width: "80px", "text-align": "right" },
                    formatter: () => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
            defaultSortAsc: false,
            locationDependend: true,
            defaultSortProperty: "fulfillDate",
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.search.addDropdownFilterFromNumericOptions(
            "states",
            this.generateOrderStateOptions("enums.OrderState"),
            i18n("order.allStates"),
            [OrderState.FullyPaid, OrderState.Closed, OrderState.NotPaid, OrderState.ClosedNotPaid],
            true
        );
        this.dataTable = new DataTable<IOrder>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao,
            tableConfig
        );
        this.dataTable.onClick(
            (m) => {
                location.href = `/order/${m.id}?lId=${m.locationId}`;
            },
            (m) => {
                window.open(`/order/${m.id}?lId=${m.locationId}`);
            }
        );

        this.dataTable.listenTo(this.search);
        portalEvents.OnLocationChanged.add(() => {
            this.dataTable.loadData(true);
        }, this);
    }

    private generateOrderStateOptions(transNameSpace: string): Array<{ value: number; label: string }> {
        const filterOptions = new Array<{ value: number; label: string }>();
        const orderStates = [
            OrderState.Open,
            OrderState.NotPaid,
            OrderState.FullyPaid,
            OrderState.ClosedNotPaid,
            OrderState.Closed,
            OrderState.TimedOut,
            OrderState.Canceled,
        ];
        orderStates.forEach((state) => {
            filterOptions.push({
                value: state.valueOf(),
                label: i18n(transNameSpace + "." + OrderState[state]),
            });
        });
        return filterOptions;
    }
}
